import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import MapComponent, { Layer, Marker, NavigationControl, Source } from "react-map-gl";
import { FreehandDrawControl } from "./freeHandDrawControl";
import { useStore } from "@roc/client-portal-shared/hooks";
import { featureCollection, point } from '@turf/helpers';
import { center } from '@turf/turf';
import { bbox } from '@turf/bbox';
import { observer } from "mobx-react";
import { CompPropertyMarker } from "./compPropertyMarker";
import { SubjectPropertyMarker } from "./subjectPropertyMarker";
import { FeatureCollection, Feature, Polygon } from 'geojson';
import { ResizeControl } from "./resizeControls";
import { HeatmapControl } from "./heatmapControl";
import type { LayerProps } from 'react-map-gl';
import { segmentAnalytics, SegmentTrackerEventCompTool } from "@roc/feature-utils";
import PropertyViewModal from "../propertyViewModal";
import { Accordion, AccordionDetails, AccordionSummary, Box, makeStyles, Typography } from "@material-ui/core";
import { ManuallyAddedComparableMarker } from "./manuallyAddedComparableMarker";
import { formatCurrency } from "@roc/ui/utils";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { ReactComponent as SubjectPropertyMarkerSVG } from "../../images/subjectPropertyMarker.svg";
import { ReactComponent as ListedTargetMarkerSVG } from "../../images/listedTargetMarker.svg";
import { ReactComponent as AppraisalMarkerSVG } from "../../images/appraisalMarker.svg";
import { ReactComponent as DistressedMarkerSVG } from "../../images/distressedMarker.svg";
import { ReactComponent as ManuallyAddedPropertyIcon } from '../../images/manuallyaddedproperty.svg';


const PropertiesMap = observer(() => {
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const { compToolStoreV2: compToolStore, environmentStore } = useStore();
  const { subjectProperty, compToolFiltersStore, isInternal, internalUsername, userStore, } = compToolStore;
  const [openViewModal, setOpenViewModal] = useState(false);
  const { compToolFeedbackStore } = compToolStore;
  const [heatmapActive, setHeatmapActive] = useState(false);
  const [polygonValue, setPolygonValue] = useState();

  const token = environmentStore.environmentVariables.mapBoxToken;

  const manuallyAddedComparables = compToolStore.manuallyAddedComparables;
  const properties = compToolStore.compResponse.properties;

  const points = useMemo(() => {
    const props = [...properties, ...manuallyAddedComparables, subjectProperty];
    return featureCollection(
      props.map((m) => point([m.data.lng, m.data.lat]))
    );
  }, [properties, subjectProperty, manuallyAddedComparables]);

  const centerPoint = center(points);
  const [longitude, latitude] = centerPoint.geometry.coordinates;

  useEffect(() => {

    if (mapLoaded && mapRef?.current && properties?.length > 0) {
      const bounds = bbox(points);

      mapRef.current.fitBounds(
        [[bounds[0], bounds[1]], [bounds[2], bounds[3]]],
        { padding: 40, duration: 1000 }
      );
    }
  }, [mapLoaded, properties, points]);

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.resize();
    }
  }, [compToolStore.showCardView, compToolStore.propertiesMapExtended]);


  const onComplete = useCallback((newPolygon) => {
    compToolFiltersStore.addDrawingPolygon(newPolygon);
  }, []);

  const onClear = useCallback(() => {
    compToolFiltersStore.setDrawingPolygons([]);
  }, [compToolFiltersStore]);

  const onStartDrawing = useCallback(() => {
    compToolFiltersStore.setDrawingPolygons([]);
  }, [compToolFiltersStore]);

  const onApply = useCallback(() => {
    if (compToolFiltersStore.drawingPolygons.length <= 0) {
      return;
    }
    compToolFiltersStore.setFiltersPolygons([...compToolFiltersStore.drawingPolygons]);
    compToolFiltersStore.setDrawingPolygons([]);
    compToolStore.applyFilters();
  }, []);

  const geoJsonDataDrawnPolygon: FeatureCollection<Polygon> = {
    type: "FeatureCollection",
    features: compToolFiltersStore.drawingPolygons.map((coords, index): Feature<Polygon> => ({
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [coords],
      },
      properties: { id: index },
    })),
  };

  const geoJsonDataFilterPolygon: FeatureCollection<Polygon> = {
    type: "FeatureCollection",
    features: compToolFiltersStore.filterPolygons.map((coords, index): Feature<Polygon> => ({
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [coords],
      },
      properties: { id: index },
    })),
  };



  const handleHeatmapClick = (active) => {
    setHeatmapActive(active);
    if (active) {
      compToolStore.fetchHeatmapData();
    } else {
      compToolStore.clearHeatmapData();
      setPolygonValue(null);
    }
  }

  const heatmapPolygons = assignColorsToPolygons(compToolStore.heatmapData?.polygons || []);
  const heatmapGeoJsonData: FeatureCollection<Polygon> = {
    type: "FeatureCollection",
    features: (heatmapPolygons).map((polygon, index): Feature<Polygon> => ({
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [polygon.geometry],
      },
      properties: {
        id: polygon.polygon_id,
        fillColor: polygon.fillColor,
        value: polygon.value
      },
    })),
  };
  const polygonLayer: LayerProps = {
    id: 'polygon-layer',
    type: 'fill',
    paint: {
      'fill-color': ['get', 'fillColor'],
      'fill-opacity': 0.5
    }
  };

  const openDialog = (property) => {
    compToolStore.setSelectedProperty(property);
    setOpenViewModal(true);
    compToolFeedbackStore.reset();
    segmentAnalytics.trackEvent({
      name: SegmentTrackerEventCompTool.COMP_TOOL_COMP_PROPERTY_CLICKED,
      userName: isInternal ? internalUsername : userStore.userInformation?.username,
      subjectAddress: subjectProperty.data.address,
      propertyAddress: property.data.address
    });
  }

  const onMouseMoveHandler = (e) => {
    const feature = e.target.queryRenderedFeatures(e.point, {
      layers: ["polygon-layer"]
    })[0];

    if (feature) {
      setPolygonValue(feature.properties.value);
    } else {
      setPolygonValue(null);
    }

  }

  return (
    <Box width="100%" height="100%" position="relative">
      <MapComponent
        ref={mapRef}
        initialViewState={{
          longitude: longitude,
          latitude: latitude,
          zoom: 17,
        }}
        mapStyle="mapbox://styles/mapbox/streets-v11"
        mapboxAccessToken={token}
        onLoad={() => setMapLoaded(true)}
        interactiveLayerIds={["heatmap-polygons"]}
        onMouseMove={onMouseMoveHandler}
      >
        <ResizeControl onClick={(maximized) => { compToolStore.setPropertiesMapExtended(maximized) }} />
        <NavigationControl position="top-left" />
        <HeatmapControl onToggle={handleHeatmapClick} />
        {heatmapGeoJsonData && (
          <Source id="heatmap-polygons" type="geojson" data={heatmapGeoJsonData}>
            <Layer
              {...polygonLayer}
            />
          </Source>
        )}

        {mapLoaded && mapRef.current?.getMap && (
          <FreehandDrawControl
            map={mapRef.current.getMap()}
            lineColor="#00bfff"
            lineWidth={3}
            onStartDrawing={onStartDrawing}
            onComplete={onComplete}
            onClear={onClear}
            onApply={onApply}
          />
        )}
        {geoJsonDataDrawnPolygon && (

          <Source id="polygon-src-draw" type="geojson" data={geoJsonDataDrawnPolygon}>
            <Layer
              id="polygon-layer-draw"
              type="fill"
              paint={{
                "fill-color": "#00bfff",
                "fill-opacity": 0.4,
              }}
            />
            <Layer
              id="polygon-border-draw"
              type="line"
              paint={{
                "line-color": "#00bfff",
                "line-width": 2,
                "line-opacity": 0.7,
              }}
            />
          </Source>

        )}

        {geoJsonDataFilterPolygon && (

          <Source id="polygon-src-filter" type="geojson" data={geoJsonDataFilterPolygon}>
            <Layer
              id="polygon-layer-filter"
              type="fill"
              paint={{
                "fill-color": "#f0e21f",
                "fill-opacity": 0.4,
              }}
            />
            <Layer
              id="polygon-border-filter"
              type="line"
              paint={{
                "line-color": "#f0e21f",
                "line-width": 2,
                "line-opacity": 0.7,
              }}
            />
          </Source>

        )}
        <Marker
          key={`subject-property-${subjectProperty.data.lat}-${subjectProperty.data.lng}`}
          longitude={subjectProperty.data.lng}
          latitude={subjectProperty.data.lat}>
          <SubjectPropertyMarker />
        </Marker>
        {manuallyAddedComparables.map((property) => (
          <Marker
            key={property.property_id}
            longitude={property.data.lng}
            latitude={property.data.lat}>
            <ManuallyAddedComparableMarker property={property} onThumbnailClick={() => openDialog(property)} isSelected={compToolStore.isPropertySelected(property.property_id)} />
          </Marker>
        ))}
        {properties.map((property) => (
          <Marker
            key={property.property_id}
            longitude={property.data.lng}
            latitude={property.data.lat}>
            <CompPropertyMarker property={property} onThumbnailClick={() => openDialog(property)} isSelected={compToolStore.isPropertySelected(property.property_id)} />
          </Marker>
        ))}
      </MapComponent>
      <PropertyViewModal open={openViewModal} property={compToolStore.selectedProperty} onClose={() => setOpenViewModal(false)} />
      {heatmapActive && (<HeatmapLegend minPrice={compToolStore.heatmapMinPricePerSqFt} maxPrice={compToolStore.heatmapMaxPricePerSqFt} polygonValue={polygonValue} />)}
      {!heatmapActive && <MapLegend />}
    </Box>
  );
});

export default PropertiesMap;

const useStylesHeatmapLegend = makeStyles(() => ({
  heatmapContainer: {
    position: "absolute",
    top: "0px",
    left: "50px",
    boxShadow: "0 0 0 2px rgba(0, 0, 0, 0.1)",
    backgroundColor: "white",
    borderRadius: "4px",
    padding: "8px",
    marginTop: "10px",
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  blockLegendContainer: {
    position: "absolute",
    top: "0px",
    right: "10px",
    boxShadow: "0 0 0 2px rgba(0, 0, 0, 0.1)",
    backgroundColor: "white",
    borderRadius: "4px",
    padding: "8px",
    marginTop: "10px",
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  palleteBar: {
    display: "flex",
    width: "400px",
    height: "15px",
    borderRadius: "100%",
    "& > div:first-child": {
      borderTopLeftRadius: 12,
      borderBottomLeftRadius: 12,
    },
    "& > div:last-child": {
      borderTopRightRadius: 12,
      borderBottomRightRadius: 12,
    },
  }
}));

interface HeatmapLegendProps {
  minPrice: number;
  maxPrice: number;
  polygonValue: number;
}


const HeatmapLegend = ({ minPrice, maxPrice, polygonValue }: HeatmapLegendProps) => {
  const classes = useStylesHeatmapLegend();

  const polValue = polygonValue;
  let polygonValueFormatted = "-";
  if (polygonValue != null && polygonValue > 0) {
    polygonValueFormatted = formatCurrency(polValue.toFixed(0), 0);
  }

  return (
    <>
      <Box className={classes.heatmapContainer}>
        <Typography variant="h5" >Price per Sq.Ft.</Typography>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2" >{formatCurrency(minPrice, 0)}</Typography>
          <Typography variant="body2" >{formatCurrency(maxPrice, 0)}</Typography>
        </Box>
        <Box className={classes.palleteBar}>
          {heatmapColorsPallete.map((color, index) => (
            <Box
              key={index}
              flex={1}
              bgcolor={color}
            />
          ))}
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2" >Lower $/Sq.Ft.</Typography>
          <Typography variant="body2" >Higher $/Sq.Ft.</Typography>
        </Box>
      </Box>
      <Box className={classes.blockLegendContainer}>
        <Typography variant="h6" >Price per Sq.Ft. (Hovered Area)</Typography>
        <Box display="flex" justifyContent="center" alignItems="center" width="100%">
          <Typography variant="body2" >{polygonValueFormatted}</Typography>
        </Box>

      </Box>
    </>
  );
}

const heatmapColorsPallete = [
  "rgba(67, 3, 83, 0.6)",
  "rgba(71, 17, 99, 0.6)",
  "rgba(71, 31, 112, 0.6)",
  "rgba(71, 45, 122, 0.6)",
  "rgba(68, 57, 130, 0.6)",
  "rgba(64, 70, 135, 0.6)",
  "rgba(59, 82, 139, 0.6)",
  "rgba(54, 93, 141, 0.6)",
  "rgba(49, 103, 142, 0.6)",
  "rgba(45, 113, 142, 0.6)",
  "rgba(41, 125, 142, 0.6)",
  "rgba(37, 134, 142, 0.6)",
  "rgba(34, 144, 141, 0.6)",
  "rgba(33, 154, 138, 0.6)",
  "rgba(32, 164, 135, 0.6)",
  "rgba(41, 173, 128, 0.6)",
  "rgba(54, 183, 121, 0.6)",
  "rgba(71, 194, 111, 0.6)",
  "rgba(95, 201, 98, 0.6)",
  "rgba(117, 208, 83, 0.6)",
  "rgba(144, 215, 67, 0.6)",
  "rgba(174, 219, 47, 0.6)",
  "rgba(201, 224, 31, 0.6)",
  "rgba(228, 228, 25, 0.6)",
  "rgba(254, 231, 36, 0.6)"
];


function assignColorsToPolygons(polygons: any[]) {
  if (!polygons || polygons.length === 0) {
    return [];
  }
  const polygonsWithValue = polygons.filter(p => p.value !== null);
  const sorted = [...polygonsWithValue].sort((a, b) => a.value - b.value);

  const valueToColorMap = new Map<number, string>();
  sorted.forEach((polygon, index) => {
    const colorIndex = Math.floor(index * (heatmapColorsPallete.length - 1) / (sorted.length - 1));
    valueToColorMap.set(polygon.value, heatmapColorsPallete[colorIndex]);
  });

  return polygons.map(polygon => ({
    ...polygon,
    fillColor: polygon.value === null ? 'rgba(150,150,150,0.4)' : valueToColorMap.get(polygon.value)
  }));
}

const useStylesMapLegend = makeStyles(() => ({
  legendContainer: {
    position: "absolute",
    top: "0px",
    left: "50px",
    boxShadow: "0 0 0 2px rgba(0, 0, 0, 0.1)",
    backgroundColor: "white",
    borderRadius: "4px",
    marginTop: "10px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "245px",
    padding: 0
  },
  accordion: {
    boxShadow: "none",
    "&.MuiPaper-elevation1": {
      boxShadow: "none",
    },
  },
  accordionSummaryContent: {
    margin: 0,
    "&.Mui-expanded": {
      margin: 0,
    },
  },
  accordionDetails: {
    padding: "0px 4px 8px 16px",
  },
  iconsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: 0,
    width: "100%"
  },
  legendItemContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%"
  },
  iconBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "50px",
  }
}));


const MapLegend = () => {

  const classes = useStylesMapLegend();

  return (

    <Box className={classes.legendContainer}>
      <Accordion classes={{ root: classes.accordion }}>
        <AccordionSummary
          classes={{ content: classes.accordionSummaryContent }}
          expandIcon={<ExpandMoreIcon />}
          aria-controls="map-legend"
          id="map-legend-header"
        >
          <Typography variant="h6">Legend</Typography>
        </AccordionSummary>
        <AccordionDetails classes={{ root: classes.accordionDetails }}>
          <Box className={classes.iconsContainer}>
            <Box className={classes.legendItemContainer}>
              <Typography>Subject Property</Typography>
              <Box className={classes.iconBox}>
                <SubjectPropertyMarkerSVG height="36px" />
              </Box>
            </Box>
            <Box className={classes.legendItemContainer}>
              <Typography>Target Property</Typography>
              <Box className={classes.iconBox}>
                <ListedTargetMarkerSVG />
              </Box>
            </Box>
            <Box className={classes.legendItemContainer}>
              <Typography>Distressed Property</Typography>
              <Box className={classes.iconBox}>
                <DistressedMarkerSVG />
              </Box>
            </Box>
            <Box className={classes.legendItemContainer}>
              <Typography>Manually Added Comp.</Typography>
              <Box className={classes.iconBox}>
                <ManuallyAddedPropertyIcon />
              </Box>
            </Box>
            <Box className={classes.legendItemContainer}>
              <Typography>Appraisal Property</Typography>
              <Box className={classes.iconBox}>
                <AppraisalMarkerSVG />
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
